/**
 * MealsForm.jsx
 * CCH Care — Phase 3 → Phase 5f
 * Last Modified: 2026-06-08
 *
 * Accordion layout: one card per meal/snack, collapsed by default.
 * The current meal (determined by time of day) auto-expands on load.
 * Signed meals stay collapsed with a ✓ Signed chip.
 * AM Snack and PM Snack use a lighter form (consumed % + notes only).
 * Core meals (breakfast / lunch / dinner) must all be signed for section complete.
 *
 * Section-level N/A (2026-06-08):
 * The care-plan isNA flag (patient_adl_flags) marks the ENTIRE Meals
 * section not-applicable — e.g. NPO / tube-fed residents whose intake is
 * not documented as meals. When isNA, the whole accordion is hidden and
 * the card renders locked with the N/A banner, matching the other
 * sections. This is DISTINCT from the per-meal "N/A – Outside Shift"
 * concept below, which only applies when the section itself is active.
 *
 * N/A – Outside Shift (per-meal, internal):
 * Meals outside the current shift's service window are auto-suggested as
 * "Not Applicable – Outside Shift" based on shiftType (AM / PM / NOC).
 * The CNA can override any suggestion. N/A meals count toward section
 * completion once acknowledged and signed.
 *
 * Late Documentation Phase 3 — Chunk 2b (2026-05-18): per-meal Amend.
 * Meals signs per-meal, not per-section, so the section-level Amend
 * button on ADLSectionCard only appears once ALL three core meals are
 * signed — which left a single signed meal (e.g. Breakfast) with no
 * correction path. Each signed MealEntry now carries its own Amend
 * button in the signed header; it forwards 'amend_meals' to onSign and
 * the existing modal handles meal/field selection. This per-meal path
 * is NOT gated on coreAllSigned — one signed meal is amendable on its
 * own. The section-level handler (handleMealsAmend) is unchanged.
 *
 * Late Documentation Phase 3 (2026-05-16): Meals has no section-level
 * Sign button (per-meal signing), so it passed onSign=undefined — which
 * also left the Amend button on a fully-signed Meals section dead. It
 * now forwards amend_ keys to onSign, but only once the section is
 * signed, so an unsigned Meals section still shows no Sign button.
 *
 * Dark mode: replaced hardcoded '#F0FBF5' with COLORS.accentLight,
 *            added background: COLORS.surface to inputStyle.
 */

import { useState, useCallback, useMemo } from 'react';
import { COLORS } from '../../config/constants';
import { saveMeal, signMeal } from '../../api/adl';
import useADLForm from '../../hooks/useADLForm';
import ADLSectionCard from '../../components/ui/ADLSectionCard';
import ADLAmendmentBanner from '../../components/ui/ADLAmendmentBanner';
import ADLSectionNA from '../../components/ui/ADLSectionNA';

import { formatTime } from '../../utils/formatters';
const PERCENT_OPTIONS = [0, 25, 50, 75, 100];
const ASSIST_OPTIONS  = ['independent', 'setup', 'minimal', 'moderate', 'total', 'refused'];
const DIET_PRESETS    = ['Regular', 'Mechanical Soft', 'Pureed', 'Liquid', 'NPO'];

// Core meals required for section completion
const CORE_MEALS = ['breakfast', 'lunch', 'dinner'];

/**
 * Meals auto-suggested as N/A based on shift type.
 * AM  = 06:00–14:00  covers Breakfast, AM Snack, Lunch
 * PM  = 14:00–22:00  covers PM Snack, Dinner
 * NOC = 22:00–06:00  covers nothing — all meals N/A
 */
const NA_BY_SHIFT = {
  AM:  ['snack_pm', 'dinner'],
  PM:  ['breakfast', 'snack_am', 'lunch'],
  NOC: ['breakfast', 'snack_am', 'lunch', 'snack_pm', 'dinner'],
};

/**
 * Meal config — order, display label, icon, snack flag, and time window.
 * Time windows determine which meal auto-expands on load.
 * startHour inclusive, endHour exclusive (24-hour decimal).
 */
const MEAL_CONFIG = [
  { type: 'breakfast', label: 'Breakfast', icon: '🌅', isSnack: false, startHour:  5,   endHour:  9.5 },
  { type: 'snack_am',  label: 'AM Snack',  icon: '🍎', isSnack: true,  startHour:  9.5, endHour: 11   },
  { type: 'lunch',     label: 'Lunch',     icon: '☀️',  isSnack: false, startHour: 11,   endHour: 13.5 },
  { type: 'snack_pm',  label: 'PM Snack',  icon: '🍊', isSnack: true,  startHour: 13.5, endHour: 16   },
  { type: 'dinner',    label: 'Dinner',    icon: '🌙', isSnack: false, startHour: 16,   endHour: 20   },
];

/** Returns the meal type matching the current local time, defaulting to dinner. */
function getCurrentMealType() {
  const now = new Date();
  const h   = now.getHours() + now.getMinutes() / 60;
  const match = MEAL_CONFIG.find(m => h >= m.startHour && h < m.endHour);
  return match ? match.type : 'dinner';
}

export default function MealsForm({
  adlRecordId,
  initialData = [],
  sectionStatus,
  amendments,
  onSign,
  disabled,
  shiftType,
  // Phase 5d N/A wiring (care-plan section flag — distinct from per-meal
  // "N/A – Outside Shift", which is internal to this form)
  isNA = false,
  naReason = '',
}) {
  const [expanded, setExpanded] = useState(() => {
    const currentType = getCurrentMealType();
    return { [currentType]: true };
  });

  // Per-meal N/A state — seeded from shiftType auto-suggest
  const [mealNA, setMealNA] = useState(() => {
    const suggested = NA_BY_SHIFT[shiftType] ?? [];
    const state = {};
    MEAL_CONFIG.forEach(cfg => {
      const existing = initialData.find(r => r.meal_type === cfg.type);
      state[cfg.type] = !existing?.signed_at && suggested.includes(cfg.type);
    });
    return state;
  });

  // Track per-meal signatures (seeded from initialData)
  const [mealSignatures, setMealSignatures] = useState(() => {
    const sigs = {};
    initialData.forEach(m => {
      if (m.signed_at) {
        sigs[m.meal_type] = {
          signed_at: m.signed_at,
          signed_by: m.signed_by_name || '',
        };
      }
    });
    return sigs;
  });

  const handleMealSigned = useCallback((mealType, data) => {
    setMealSignatures(prev => ({
      ...prev,
      [mealType]: { signed_at: data.signed_at, signed_by: '' },
    }));
    setExpanded(prev => ({ ...prev, [mealType]: false }));
    if (data.section_signed) {
      onSign?.('meals');
    }
  }, [onSign]);

  const toggleExpanded = useCallback((mealType) => {
    setExpanded(prev => ({ ...prev, [mealType]: !prev[mealType] }));
  }, []);

  const toggleNA = useCallback((mealType, value) => {
    setMealNA(prev => ({ ...prev, [mealType]: value }));
  }, []);

  // Meals has no section-level sign (each meal is signed individually),
  // so ADLSectionCard receives onSign=undefined while unsigned to keep a
  // dead "Sign Section" button from appearing. Once ALL three core meals
  // are signed the section card is amendable too — forward amend_ keys.
  // This gated handler is the section-level path; the per-meal path
  // below (handleMealAmend) is the primary one and is NOT gated.
  const handleMealsAmend = useCallback((key) => {
    if (typeof key === 'string' && key.startsWith('amend_')) onSign?.(key);
  }, [onSign]);

  // Chunk 2b: per-meal Amend. A single signed meal must be amendable on
  // its own, without waiting for the other two — so this is ungated.
  // Each signed MealEntry calls this from its own Amend button.
  const handleMealAmend = useCallback(() => {
    onSign?.('amend_meals');
  }, [onSign]);

  const coreAllSigned   = CORE_MEALS.every(m => !!mealSignatures[m]);
  const effectiveStatus = isNA ? 'na' : (coreAllSigned ? 'signed' : (sectionStatus ?? 'draft'));
  const hasAnySuggested = Object.values(mealNA).some(Boolean);

  return (
    <ADLSectionCard
      title="Meals"
      icon="🍽️"
      sectionKey="meals"
      status={effectiveStatus}
      hasAmendments={amendments?.some(a => a.section === 'meals')}
      onSign={isNA ? undefined : (effectiveStatus === 'signed' ? handleMealsAmend : undefined)}
      disabled={disabled || isNA}
    >
      <ADLAmendmentBanner amendments={amendments} section="meals" />

      {/* Section-level N/A banner (care plan flag — whole section N/A) */}
      {isNA && (
        <ADLSectionNA
          section="meals"
          isNA={isNA}
          naReason={naReason}
          supportsAttestation={false}
          isSectionSigned={false}
        />
      )}

      {!isNA && (
        <>
          {coreAllSigned && (
            <div style={{
              background: COLORS.accentLight,
              border: `1px solid ${COLORS.accentAlpha}`,
              borderRadius: 8, padding: '8px 12px', marginBottom: 12,
              fontSize: 12, color: COLORS.accent, fontWeight: 600,
            }}>
              ✓ All three meals signed — section complete
            </div>
          )}

          {!coreAllSigned && !disabled && hasAnySuggested && (
            <div style={{
              background: COLORS.primaryLight, border: `1px solid ${COLORS.primaryAlpha}`,
              borderRadius: 8, padding: '8px 12px', marginBottom: 12,
              fontSize: 12, color: COLORS.primary,
            }}>
              💡 Some meals have been pre-marked as <strong>N/A – Outside Shift</strong> based on your shift type.
              You can override any of these before signing.
            </div>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {MEAL_CONFIG.map(cfg => {
              const existing   = initialData.find(r => r.meal_type === cfg.type) ?? {};
              const signature  = mealSignatures[cfg.type] ?? null;
              const isExpanded = !!expanded[cfg.type];
              const mealIsNA   = !!mealNA[cfg.type];

              return (
                <MealEntry
                  key={cfg.type}
                  adlRecordId={adlRecordId}
                  config={cfg}
                  initialData={{
                    percent_consumed: existing.percent_consumed ?? null,
                    diet_type:        existing.diet_type        ?? '',
                    assist_level:     existing.assist_level     ?? null,
                    fluid_intake_ml:  existing.fluid_intake_ml  ?? '',
                    supplement:       existing.supplement       ?? '',
                    refused:          existing.refused          ?? false,
                    notes:            existing.notes            ?? '',
                  }}
                  isMealSigned={!!signature}
                  signature={signature}
                  isExpanded={isExpanded}
                  isNA={mealIsNA}
                  onToggle={() => toggleExpanded(cfg.type)}
                  onNAChange={val => toggleNA(cfg.type, val)}
                  disabled={disabled || !!signature}
                  onMealSigned={handleMealSigned}
                  onAmend={handleMealAmend}
                />
              );
            })}
          </div>

          {!coreAllSigned && !disabled && (
            <p style={{ fontSize: 11, color: COLORS.textMuted, margin: '10px 0 0', textAlign: 'center' }}>
              Sign each core meal after documenting it. All three must be signed to complete this section.
            </p>
          )}
        </>
      )}
    </ADLSectionCard>
  );
}

// ── Individual meal entry (accordion card) ────────────────────
function MealEntry({
  adlRecordId, config, initialData,
  isMealSigned, signature, isExpanded, isNA,
  onToggle, onNAChange,
  disabled, onMealSigned, onAmend,
}) {
  const { type: mealType, label: mealLabel, icon, isSnack } = config;
  const isCore = CORE_MEALS.includes(mealType);

  const saveFn = useCallback(async (data) => {
    await saveMeal(adlRecordId, { meal_type: mealType, ...data });
  }, [adlRecordId, mealType]);

  const { formData, setField, isSaving, isSaved } = useADLForm(
    initialData,
    saveFn,
    { disabled: disabled || isMealSigned },
  );

  const [signing,   setSigning]   = useState(false);
  const [signError, setSignError] = useState('');

  const refused = !!formData.refused;

  const hasData = refused || isNA ||
    formData.percent_consumed !== null ||
    formData.assist_level     !== null;

  function handleRefusedToggle() {
    if (!refused) onNAChange(false);
    setField('refused', !refused);
  }

  function handleNAToggle() {
    if (!isNA) setField('refused', false);
    onNAChange(!isNA);
  }

  async function handleSign() {
    setSignError('');
    if (!hasData) {
      setSignError('Please document this meal before signing.');
      return;
    }
    setSigning(true);
    try {
      if (isNA && !formData.notes) {
        await saveMeal(adlRecordId, {
          meal_type: mealType,
          notes: 'Not applicable – outside shift hours',
        });
      }
      const data = await signMeal(adlRecordId, mealType);
      if (!data.success) throw new Error(data.error || 'Sign failed');
      onMealSigned(mealType, data);
    } catch (e) {
      setSignError(e?.response?.data?.error || e.message || 'Failed to sign');
    } finally {
      setSigning(false);
    }
  }

  // Chunk 2b: per-meal Amend. stopPropagation so the tap opens the
  // amendment modal without also toggling the accordion (the header
  // div this button sits inside carries onClick={onToggle}).
  function handleAmendClick(e) {
    e.stopPropagation();
    onAmend?.();
  }

  const signedTime = useMemo(() => {
    if (!signature?.signed_at) return null;
    const ts = signature.signed_at;
    return formatTime(ts);
  }, [signature]);

  const cardBg     = isMealSigned ? COLORS.accentLight : isNA ? COLORS.primaryLight : COLORS.bg;
  const cardBorder = isMealSigned
    ? COLORS.accentAlpha
    : isNA ? COLORS.primaryAlpha : COLORS.border;

  return (
    <div style={{ borderRadius: 10, border: `1px solid ${cardBorder}`, background: cardBg, overflow: 'hidden' }}>
      {/* ── Accordion header ── */}
      <div
        role="button"
        tabIndex={0}
        onClick={onToggle}
        onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && onToggle()}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '11px 14px', cursor: 'pointer', userSelect: 'none',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 16 }}>{icon}</span>
          <span style={{ fontSize: 14, fontWeight: 700, color: COLORS.text }}>{mealLabel}</span>
          {isCore && (
            <span style={{ fontSize: 10, color: COLORS.textMuted, fontWeight: 400 }}>(required)</span>
          )}
          {isMealSigned && signedTime && (
            <span style={{ fontSize: 11, color: COLORS.accent, fontWeight: 600 }}>· Signed {signedTime}</span>
          )}
          {!isMealSigned && isNA && (
            <span style={{
              fontSize: 10, fontWeight: 700, color: COLORS.primary,
              background: 'rgba(27,107,147,0.12)', padding: '2px 7px', borderRadius: 4,
            }}>
              N/A – Outside Shift
            </span>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {isSaving && <span style={{ fontSize: 11, color: COLORS.textMuted }}>Saving…</span>}
          {isSaved && !isMealSigned && (
            <span style={{ fontSize: 11, color: COLORS.accent, fontWeight: 600 }}>✓ Saved</span>
          )}
          {isMealSigned ? (
            <>
              {/* Per-meal Amend — the only correction path for a signed meal */}
              <button
                onClick={handleAmendClick}
                style={{
                  fontSize: 11, fontWeight: 700, color: COLORS.warning,
                  background: COLORS.surface, border: `1px solid ${COLORS.warning}`,
                  padding: '3px 10px', borderRadius: 20, cursor: 'pointer',
                }}
              >
                Amend
              </button>
              <span style={{
                fontSize: 11, fontWeight: 700, color: COLORS.accent,
                background: 'rgba(46,204,113,0.15)', padding: '3px 10px', borderRadius: 20,
              }}>✓ Signed</span>
            </>
          ) : (
            <span style={{
              fontSize: 18, color: COLORS.textMuted,
              transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s', display: 'inline-block',
            }}>▾</span>
          )}
        </div>
      </div>

      {/* ── Expanded body ── */}
      {isExpanded && !isMealSigned && (
        <div style={{ padding: '0 14px 14px' }}>

          {/* Status toggles */}
          {!disabled && (
            <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', marginBottom: 10 }}>
              <button
                onClick={handleNAToggle}
                style={{
                  padding: '4px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, cursor: 'pointer',
                  background: isNA ? COLORS.primaryLight : COLORS.surface,
                  color: isNA ? COLORS.primary : COLORS.textMuted,
                  border: `1px solid ${isNA ? COLORS.primary : COLORS.border}`,
                }}
              >
                {isNA ? '✕ N/A Outside Shift' : 'N/A – Outside Shift'}
              </button>
              <button
                onClick={handleRefusedToggle}
                style={{
                  padding: '4px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, cursor: 'pointer',
                  background: refused ? COLORS.dangerLight : COLORS.surface,
                  color: refused ? COLORS.danger : COLORS.textMuted,
                  border: `1px solid ${refused ? COLORS.danger : COLORS.border}`,
                }}
              >
                {refused ? '✕ Refused' : 'Mark Refused'}
              </button>
            </div>
          )}

          {isNA && (
            <div style={{
              background: 'rgba(27,107,147,0.08)', border: `1px solid ${COLORS.primaryAlpha}`,
              borderRadius: 8, padding: '10px 12px', marginBottom: 10,
              fontSize: 13, color: COLORS.primary,
            }}>
              <strong>Not Applicable – Outside Shift Hours</strong>
              <div style={{ fontSize: 12, marginTop: 3, color: COLORS.textMuted }}>
                This meal falls outside the current shift window. Sign to acknowledge.
              </div>
            </div>
          )}

          {refused && !isNA && (
            <div style={{ fontSize: 13, color: COLORS.danger, fontStyle: 'italic', marginBottom: 10 }}>
              Patient refused this meal.
            </div>
          )}

          {!refused && !isNA && (
            <>
              <div style={{ marginBottom: 10 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: COLORS.textMuted, marginBottom: 6 }}>% Consumed</div>
                <div style={{ display: 'flex', gap: 6 }}>
                  {PERCENT_OPTIONS.map(pct => {
                    const selected = formData.percent_consumed === pct;
                    return (
                      <button key={pct}
                        onClick={() => !disabled && setField('percent_consumed', selected ? null : pct)}
                        style={{
                          flex: 1, padding: '7px 0', borderRadius: 8, fontSize: 13, fontWeight: 700,
                          cursor: disabled ? 'default' : 'pointer',
                          background: selected ? COLORS.primary : COLORS.surface,
                          color: selected ? '#fff' : COLORS.textMuted,
                          border: `1px solid ${selected ? COLORS.primary : COLORS.border}`,
                        }}
                      >
                        {pct}%
                      </button>
                    );
                  })}
                </div>
              </div>

              {!isSnack && (
                <>
                  <div style={{ marginBottom: 10 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: COLORS.textMuted, marginBottom: 6 }}>Diet Type</div>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      {DIET_PRESETS.map(diet => {
                        const selected = formData.diet_type === diet;
                        return (
                          <button key={diet}
                            onClick={() => !disabled && setField('diet_type', selected ? '' : diet)}
                            style={{
                              padding: '5px 10px', borderRadius: 6, fontSize: 11, fontWeight: 600,
                              cursor: disabled ? 'default' : 'pointer',
                              background: selected ? COLORS.primaryLight : COLORS.surface,
                              color: selected ? COLORS.primary : COLORS.textMuted,
                              border: `1px solid ${selected ? COLORS.primary : COLORS.border}`,
                            }}
                          >
                            {diet}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div style={{ marginBottom: 10 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: COLORS.textMuted, marginBottom: 6 }}>Assist Level</div>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      {ASSIST_OPTIONS.map(level => {
                        const selected = formData.assist_level === level;
                        return (
                          <button key={level}
                            onClick={() => !disabled && setField('assist_level', selected ? null : level)}
                            style={{
                              padding: '5px 10px', borderRadius: 6, fontSize: 11, fontWeight: 600,
                              cursor: disabled ? 'default' : 'pointer',
                              background: selected ? COLORS.primary : COLORS.surface,
                              color: selected ? '#fff' : COLORS.textMuted,
                              border: `1px solid ${selected ? COLORS.primary : COLORS.border}`,
                            }}
                          >
                            {level.charAt(0).toUpperCase() + level.slice(1)}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: COLORS.textMuted, marginBottom: 4 }}>Fluids at meal (mL)</div>
                      <input
                        type="number" min="0" max="2000"
                        value={formData.fluid_intake_ml ?? ''}
                        onChange={e => !disabled && setField('fluid_intake_ml', e.target.value ? parseInt(e.target.value) : null)}
                        placeholder="0" disabled={disabled}
                        autoComplete="off"
                        style={inputStyle}
                      />
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: COLORS.textMuted, marginBottom: 4 }}>Supplement</div>
                      <input
                        type="text"
                        value={formData.supplement ?? ''}
                        onChange={e => !disabled && setField('supplement', e.target.value)}
                        placeholder="e.g. Ensure" disabled={disabled}
                        autoComplete="off"
                        style={inputStyle}
                      />
                    </div>
                  </div>
                </>
              )}

              <input
                type="text"
                value={formData.notes ?? ''}
                onChange={e => !disabled && setField('notes', e.target.value)}
                placeholder="Notes (optional)" disabled={disabled}
                autoComplete="off"
                style={{ ...inputStyle, marginBottom: 10 }}
              />
            </>
          )}

          {!disabled && (
            <>
              {signError && (
                <div style={{
                  fontSize: 12, color: COLORS.danger, marginBottom: 6,
                  padding: '4px 8px', background: COLORS.dangerLight, borderRadius: 6,
                }}>
                  {signError}
                </div>
              )}
              <button
                onClick={handleSign}
                disabled={signing || !hasData}
                style={{
                  width: '100%', padding: '9px 0', borderRadius: 8,
                  fontSize: 12, fontWeight: 700, border: 'none',
                  background: isNA
                    ? COLORS.primary
                    : refused ? COLORS.danger : hasData ? COLORS.primary : COLORS.border,
                  color: hasData ? '#fff' : COLORS.textMuted,
                  cursor: signing || !hasData ? 'not-allowed' : 'pointer',
                  opacity: signing ? 0.7 : 1, transition: 'all 0.15s',
                }}
              >
                {signing
                  ? 'Signing…'
                  : isNA
                    ? '✓ Confirm – Not Applicable This Shift'
                    : refused
                      ? `✓ Confirm Refusal — Sign ${mealLabel}`
                      : `✓ Confirm & Sign ${mealLabel}`}
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

const inputStyle = {
  width: '100%', padding: '7px 10px', borderRadius: 6,
  border: `1px solid ${COLORS.border}`, fontSize: 13,
  color: COLORS.text, background: COLORS.surface,
  boxSizing: 'border-box', fontFamily: 'inherit',
};